// frontend/src/components/analyticsMap/ComparePaneColumn.tsx
import { useEffect, useRef } from 'react'
import { Button, NativeSelect, Text, VStack, Field } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi'
import { METRIC_OPTIONS, METRIC_GROUP_LABELS, UNION_LAYER_OPTIONS } from '../../types/geoAnalytics'
import type { MetricGroup } from '../../types/geoAnalytics'
import type { FilterFieldMetadata, FilterGroup, TagOption } from '../../types/contactList'
import { familyYears, visibleMetricOptions } from './metricFamilies'
import { filterGroupsToRequests, seedFilterGroups } from './comparePane'
import type { ComparePaneSpec, ComparePaneSource } from './comparePane'
import { FilterGroupCard } from '../listBuilder/FilterGroupCard'
import { useSavedSearches, useSavedSearchDetail } from '../../hooks/useSavedSearch'
import { useContactLists } from '../../hooks/useContactList'
import { useListFilters } from '../../hooks/useAnalyticsMap'

const METRIC_GROUP_ORDER: MetricGroup[] = ['elections', 'demographics', 'terrain']

interface ComparePaneColumnProps {
  title: string
  spec: ComparePaneSpec
  onChange: (next: ComparePaneSpec) => void
  availableMetricKeys: string[]
  fields: FilterFieldMetadata[]
  tags: TagOption[]
}

export function ComparePaneColumn({ title, spec, onChange, availableMetricKeys, fields, tags }: ComparePaneColumnProps) {
  const families = familyYears(availableMetricKeys)
  const pickerOptions = availableMetricKeys.length > 0
    ? visibleMetricOptions(METRIC_OPTIONS, availableMetricKeys)
    : METRIC_OPTIONS
  const metricOption = spec.source === 'metric'
    ? METRIC_OPTIONS.find(o => o.key === spec.metricOptionKey) ?? null
    : null
  const optionYears = metricOption?.requiresYear
    ? (families[metricOption.key]?.length ? families[metricOption.key] : metricOption.yearOptions ?? [])
    : []

  // --- universe seeding (fetch the picked population's filters, copy in once) ---
  const savedSearches = useSavedSearches()
  const lists = useContactLists()
  const savedDetail = useSavedSearchDetail(spec.source === 'universe' ? spec.seedSavedSearchId ?? undefined : undefined)
  const listFilters = useListFilters(spec.source === 'universe' ? spec.seedListId : null)
  const seededRef = useRef<string | null>(null)

  useEffect(() => {
    if (spec.source !== 'universe') return
    if (spec.seedSavedSearchId && savedDetail.data && seededRef.current !== `s:${spec.seedSavedSearchId}`) {
      seededRef.current = `s:${spec.seedSavedSearchId}`
      onChange({ ...spec, filterGroups: seedFilterGroups(filterGroupsToRequests(savedDetail.data.filterGroups)) })
    } else if (spec.seedListId && listFilters.data && seededRef.current !== `l:${spec.seedListId}`) {
      seededRef.current = `l:${spec.seedListId}`
      onChange({ ...spec, filterGroups: seedFilterGroups(listFilters.data.filterGroups) })
    }
  }, [spec, savedDetail.data, listFilters.data, onChange])

  const setSource = (source: ComparePaneSource) => onChange({ ...spec, source })

  const addFilterGroup = () => {
    const next: FilterGroup = { groupIndex: spec.filterGroups.length, filters: [] }
    onChange({ ...spec, filterGroups: [...spec.filterGroups, next] })
  }
  const updateGroup = (i: number, g: FilterGroup) => {
    const groups = [...spec.filterGroups]
    groups[i] = g
    onChange({ ...spec, filterGroups: groups })
  }
  const removeGroup = (i: number) => {
    const next = spec.filterGroups.filter((_, idx) => idx !== i)
    onChange({ ...spec, filterGroups: next.map((g, idx) => ({ ...g, groupIndex: idx })) })
  }

  return (
    <VStack gap={2} align="stretch" flex={1} minW={0}>
      <Text fontSize="xs" fontWeight="bold">{title}</Text>

      <Field.Root>
        <Field.Label fontSize="xs" mb={1}>Source</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            aria-label={`${title} source`}
            value={spec.source}
            onChange={e => setSource(e.target.value as ComparePaneSource)}>
            <option value="metric">Metric</option>
            <option value="union">Union layer</option>
            <option value="universe">My universe (filters)</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      {spec.source === 'metric' && (
        <>
          <Field.Root>
            <Field.Label fontSize="xs" mb={1}>Metric</Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                aria-label={`${title} metric`}
                value={spec.metricOptionKey ?? ''}
                onChange={e => {
                  const key = e.target.value || null
                  const opt = key ? METRIC_OPTIONS.find(o => o.key === key) ?? null : null
                  const years = key ? families[key] ?? [] : []
                  onChange({
                    ...spec,
                    metricOptionKey: key,
                    year: opt?.requiresYear ? (years[years.length - 1] ?? opt.yearOptions?.[opt.yearOptions.length - 1] ?? null) : null,
                  })
                }}>
                <option value="">None</option>
                {METRIC_GROUP_ORDER.map(group => {
                  const opts = pickerOptions.filter(o => o.group === group)
                  if (opts.length === 0) return null
                  return (
                    <optgroup key={group} label={METRIC_GROUP_LABELS[group]}>
                      {opts.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </optgroup>
                  )
                })}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          {metricOption?.requiresYear && optionYears.length > 0 && (
            <Field.Root>
              <Field.Label fontSize="xs" mb={1}>Year</Field.Label>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  aria-label={`${title} year`}
                  value={spec.year ?? ''}
                  onChange={e => onChange({ ...spec, year: Number(e.target.value) })}>
                  {optionYears.map(y => <option key={y} value={y}>{y}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          )}
        </>
      )}

      {spec.source === 'union' && (
        <Field.Root>
          <Field.Label fontSize="xs" mb={1}>Union layer</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              aria-label={`${title} union layer`}
              value={spec.metricOptionKey ?? ''}
              onChange={e => onChange({ ...spec, metricOptionKey: e.target.value || null })}>
              <option value="">None</option>
              {UNION_LAYER_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
      )}

      {spec.source === 'universe' && (
        <>
          <Field.Root>
            <Field.Label fontSize="xs" mb={1}>Saved search</Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                aria-label={`${title} saved search`}
                value={spec.seedSavedSearchId ?? ''}
                onChange={e => {
                  const value = e.target.value || null
                  if (!value) seededRef.current = null
                  onChange({ ...spec, seedSavedSearchId: value, seedListId: null })
                }}>
                <option value="">Choose a saved search…</option>
                {(savedSearches.data ?? []).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="xs" mb={1}>…or list</Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                aria-label={`${title} list`}
                value={spec.seedListId ?? ''}
                onChange={e => {
                  const value = e.target.value || null
                  if (!value) seededRef.current = null
                  onChange({ ...spec, seedListId: value, seedSavedSearchId: null })
                }}>
                <option value="">Choose a list…</option>
                {(lists.data ?? []).map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          <VStack gap={2} align="stretch">
            {spec.filterGroups.map((g, i) => (
              <FilterGroupCard key={g.groupIndex} group={g} fields={fields} tags={tags}
                onChange={updated => updateGroup(i, updated)} onRemove={() => removeGroup(i)}
                canRemove={spec.filterGroups.length > 1} />
            ))}
            <Button size="xs" variant="outline" onClick={addFilterGroup}><FiPlus />Add filter group</Button>
          </VStack>
          <Text fontSize="xs" color="gray.500">Counts your matching contacts per area.</Text>
        </>
      )}
    </VStack>
  );
}
